import debounce from "lodash.debounce";
import {Point, type Editor, type Operation} from "slate";

import {setCoeditorDocumentVariables} from "@khan/guide-coeditor-core";
import {isKhanmigoEditing} from "@khan/guide-math-editor";

import {editorToMarkdownComponents} from "../../slate-to-markdown.ts";
import {markUnsavedChanges} from "../helpers/insertion-helpers.ts";

/**
 * This is a Slate plugin that adds support for setting coediting-specific
 * prompt variables on relevant changes within the editor.
 */
export const withChangeTracking = (editor: Editor): Editor => {
    const {onChange, setSelection} = editor;

    editor.setSelection = (newSelection) => {
        if (
            isKhanmigoEditing(editor) &&
            newSelection &&
            newSelection.anchor &&
            editor.selection &&
            Point.isBefore(newSelection.anchor, editor.selection.anchor)
        ) {
            // NOTE(reid): This works around a tricky case where, while
            // Khanmigo is typing, the user can click to either side of the
            // document (outside of the document itself) and move the cursor,
            // even though the document itself is read-only. Moving the cursor
            // while Khanmigo is typing messes things up a lot: now, text is
            // inserted at the wrong place and things look wrong.
            //
            // There may be a better solution here, but what this does is
            // intercept any calls to `setSelection` that happen (1) while
            // Khanmigo is editing and (2) when the selection would jump to
            // a point _before_ where it currently is - and then blocks this.
            // This effectively prevents you from somehow setting the selection
            // to an earlier point in the document while it's read-only.
            return;
        }

        setSelection(newSelection);
    };

    editor.onChange = (options) => {
        onChange(options);

        if (isTrackedOperation(options?.operation)) {
            markUnsavedChanges(editor);
            splitDocumentIntoVariables(editor);
        }
    };

    return editor;
};

const isTrackedOperation = (operation?: Operation): boolean => {
    if (!operation) {
        return false;
    }

    switch (operation.type) {
        case "set_selection":
        case "insert_text":
        case "remove_text":
        case "insert_node":
        case "remove_node":
        case "split_node":
            return true;
        case "set_node":
            return !("_" in operation.newProperties);
        default:
            return false;
    }
};

// A debounced function to ensure that we only process the editor contents
// after a rapid-fire series of changes have settled. For example, rather than
// run once after every keystroke of a sentence, we wait until typing has been
// idle for 300ms and then process the document.
const splitDocumentIntoVariables = debounce((editor: Editor) => {
    const {prefix, selection, suffix} = editorToMarkdownComponents(editor);
    setCoeditorDocumentVariables(prefix, selection, suffix);
}, 300);
